/**
 * Request/response contracts shared by the API (validation) and the web app (forms, types).
 * Keeping them in one place means the browser and server can never disagree about the rules.
 */
import { z } from "zod";

export * from "./seo.js";
export * from "./site.js";

/** Languages every user-facing surface (web, emails, validation messages) is available in. */
export const LOCALES = ["en", "da"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";
export const localeSchema = z.enum(LOCALES);

/**
 * Custom validation messages are codes, not sentences. The API translates them
 * (and Zod's built-in messages) into the request's language before responding,
 * so the browser never has to guess what a message means.
 */
export const VALIDATION_MESSAGES = {
  INVALID_SLUG: {
    en: "Use lowercase letters, numbers and single hyphens",
    da: "Brug små bogstaver, tal og enkelte bindestreger",
  },
  UNKNOWN_TIMEZONE: { en: "Unknown time zone", da: "Ukendt tidszone" },
  AT_LEAST_ONE_FIELD: { en: "Provide at least one field", da: "Angiv mindst ét felt" },
  INVALID_SLOT_INTERVAL: {
    en: "Must be 5, 10, 15, 20, 30 or 60 minutes",
    da: "Skal være 5, 10, 15, 20, 30 eller 60 minutter",
  },
  END_BEFORE_START: { en: "End must be after start", da: "Slut skal være efter start" },
  OVERLAPPING_BLOCKS: { en: "Working-hour blocks on the same day overlap", da: "Arbejdstider på samme dag overlapper" },
  TO_BEFORE_FROM: { en: "Must not be before the start date", da: "Må ikke være før startdatoen" },
  WINDOW_TOO_LARGE: { en: "The date range is too long", da: "Datointervallet er for langt" },
  INVALID_PHONE: { en: "Use digits, spaces and + ( ) - only", da: "Brug kun tal, mellemrum og + ( ) -" },
  TERMS_NOT_ACCEPTED: {
    en: "Accept the terms of service and data processing agreement to continue",
    da: "Accepter handelsbetingelserne og databehandleraftalen for at fortsætte",
  },
} as const;
export type ValidationMessageCode = keyof typeof VALIDATION_MESSAGES;

export const PASSWORD_MIN_LENGTH = 12;
// Upper bound keeps Argon2 hashing cost bounded for hostile input.
export const PASSWORD_MAX_LENGTH = 128;

const email = z.string().trim().toLowerCase().email().max(254);
const password = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
const personName = z.string().trim().min(1).max(120);
// Creating a clinic accepts the terms and the data processing agreement (TERMS_VERSION).
const acceptTerms = z.literal(true, { error: "TERMS_NOT_ACCEPTED" });

export const clinicSlug = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(48)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "INVALID_SLUG");

export const signupSchema = z.object({
  clinicName: z.string().trim().min(2).max(120),
  clinicSlug,
  fullName: personName,
  email,
  password,
  acceptTerms,
});
export type SignupInput = z.infer<typeof signupSchema>;

export const loginSchema = z.object({
  email,
  // No length rules on login: rules are enforced at signup; here we only cap the size.
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const passwordResetRequestSchema = z.object({ email, locale: localeSchema.optional() });
export const passwordResetConfirmSchema = z.object({
  token: z.string().min(20).max(200),
  password,
});

export const updateProfileSchema = z.object({ fullName: personName });
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  newPassword: password,
});

export const switchClinicSchema = z.object({ clinicId: z.string().uuid() });

export const SLOT_INTERVALS = [5, 10, 15, 20, 30, 60] as const;

export const updateClinicSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    timezone: z
      .string()
      .min(1)
      .max(64)
      .refine((tz) => isValidTimeZone(tz), "UNKNOWN_TIMEZONE"),
    customerAccountsEnabled: z.boolean(),
    // Language of the public booking page until the visitor picks one.
    defaultLocale: localeSchema,
    // Public directory listing and contact details shown there and on the booking page.
    listed: z.boolean(),
    address: z.string().trim().max(300),
    phone: z.string().trim().max(32),
    description: z.string().trim().max(1000),
    // Send SMS reminders as well as email (needs an SMS provider configured on the server).
    smsRemindersEnabled: z.boolean(),
    // Booking rules (CB-12). Minutes between offered start times.
    slotIntervalMinutes: z
      .number()
      .int()
      .refine((v) => (SLOT_INTERVALS as readonly number[]).includes(v), "INVALID_SLOT_INTERVAL"),
    // Online bookings must start at least this far in the future.
    minNoticeMinutes: z.number().int().min(0).max(10_080),
    // Online bookings may be made at most this many days ahead.
    maxAdvanceDays: z.number().int().min(1).max(365),
    // Customers may cancel online until this long before the start.
    cancellationNoticeMinutes: z.number().int().min(0).max(10_080),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "AT_LEAST_ONE_FIELD");

// Zod applies .default() even inside .partial(), so update schemas are built from
// the field set WITHOUT defaults: an omitted field must stay omitted, not reset.
const serviceFields = {
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000),
  durationMinutes: z
    .number()
    .int()
    .min(5)
    .max(24 * 60),
  priceCents: z.number().int().min(0).max(100_000_000),
  currency: z
    .string()
    .length(3)
    .regex(/^[A-Z]{3}$/),
};
export const createServiceSchema = z.object({
  ...serviceFields,
  description: serviceFields.description.default(""),
  currency: serviceFields.currency.default("DKK"),
});
export const updateServiceSchema = z
  .object({ ...serviceFields, active: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "AT_LEAST_ONE_FIELD");

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

export type MembershipRole = "owner" | "admin" | "practitioner";

export interface UserDto {
  id: string;
  email: string;
  fullName: string;
}

export interface MembershipDto {
  clinicId: string;
  clinicName: string;
  clinicSlug: string;
  role: MembershipRole;
}

export interface MeDto {
  user: UserDto;
  activeClinicId: string | null;
  memberships: MembershipDto[];
}

export interface ClinicDto {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  customerAccountsEnabled: boolean;
  defaultLocale: Locale;
  listed: boolean;
  address: string;
  phone: string;
  description: string;
  smsRemindersEnabled: boolean;
  slotIntervalMinutes: number;
  minNoticeMinutes: number;
  maxAdvanceDays: number;
  cancellationNoticeMinutes: number;
  status: ClinicStatus;
  subscription: ClinicSubscriptionDto | null;
}

export interface ServiceDto {
  id: string;
  name: string;
  description: string;
  durationMinutes: number;
  priceCents: number;
  currency: string;
  active: boolean;
}

export interface Page<T> {
  items: T[];
  limit: number;
  offset: number;
  total: number;
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Booking core (CB-2): practitioners, schedules, customers, bookings.
// ---------------------------------------------------------------------------

const uuid = z.string().uuid();
const isoDateTime = z.iso.datetime({ offset: true });
const isoDate = z.iso.date();

/** Longest window the public availability endpoint serves per request. */
export const AVAILABILITY_MAX_DAYS = 31;
/** Longest window the staff bookings list serves per request. */
export const BOOKINGS_LIST_MAX_DAYS = 92;

/** ISO weekday: 1 = Monday ... 7 = Sunday. Minutes are since local midnight. */
export const workingHoursBlockSchema = z
  .object({
    weekday: z.number().int().min(1).max(7),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
  })
  .refine((b) => b.startMinute < b.endMinute, { message: "END_BEFORE_START", path: ["endMinute"] });

export const workingHoursSchema = z
  .object({ blocks: z.array(workingHoursBlockSchema).max(50) })
  .superRefine((v, ctx) => {
    const sorted = [...v.blocks].sort((a, b) => a.weekday - b.weekday || a.startMinute - b.startMinute);
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1]!;
      const cur = sorted[i]!;
      if (prev.weekday === cur.weekday && cur.startMinute < prev.endMinute) {
        ctx.addIssue({ code: "custom", path: ["blocks"], message: "OVERLAPPING_BLOCKS" });
        return;
      }
    }
  });

export const createPractitionerSchema = z.object({
  name: personName,
  title: z.string().trim().max(120).default(""),
  serviceIds: z.array(uuid).max(200).default([]),
  workingHours: z.array(workingHoursBlockSchema).max(50).optional(),
});
export const updatePractitionerSchema = z
  .object({
    name: personName,
    title: z.string().trim().max(120),
    active: z.boolean(),
    serviceIds: z.array(uuid).max(200),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "AT_LEAST_ONE_FIELD");

export const createTimeOffSchema = z
  .object({
    startsAt: isoDateTime,
    endsAt: isoDateTime,
    reason: z.string().trim().max(500).default(""),
  })
  .refine((v) => Date.parse(v.endsAt) > Date.parse(v.startsAt), { message: "END_BEFORE_START", path: ["endsAt"] });

export const dateRangeQuerySchema = z.object({ from: isoDateTime, to: isoDateTime });

export const availabilityQuerySchema = z
  .object({
    serviceId: uuid,
    practitionerId: uuid.optional(),
    from: isoDate,
    to: isoDate,
  })
  .superRefine((v, ctx) => {
    const days = (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000;
    if (days < 0) ctx.addIssue({ code: "custom", path: ["to"], message: "TO_BEFORE_FROM" });
    else if (days >= AVAILABILITY_MAX_DAYS) ctx.addIssue({ code: "custom", path: ["to"], message: "WINDOW_TOO_LARGE" });
  });

export const bookingCustomerSchema = z.object({
  fullName: personName,
  email,
  phone: z
    .string()
    .trim()
    .max(32)
    .regex(/^[+0-9 ()./-]*$/, "INVALID_PHONE")
    .default(""),
});

/** Public booking by a customer. Omit practitionerId for "any available practitioner". */
export const createPublicBookingSchema = z.object({
  serviceId: uuid,
  practitionerId: uuid.optional(),
  startsAt: isoDateTime,
  customer: bookingCustomerSchema,
  notes: z.string().trim().max(1000).default(""),
  /** Language for this booking's emails; defaults to the clinic's default language. */
  locale: localeSchema.optional(),
});

/** Move a booking to another offered slot (optionally another practitioner). */
export const rescheduleBookingSchema = z.object({
  startsAt: isoDateTime,
  practitionerId: uuid.optional(),
});

/** Booking entered by staff: notice/advance rules do not apply, overlap rules do. */
export const createStaffBookingSchema = createPublicBookingSchema.extend({
  practitionerId: uuid,
  sendConfirmation: z.boolean().default(true),
});

export const cancelBookingSchema = z.object({ reason: z.string().trim().max(500).default("") });

export const updateBookingSchema = z
  .object({
    status: z.enum(["completed", "no_show"]),
    notes: z.string().trim().max(2000),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "AT_LEAST_ONE_FIELD");

export const BOOKING_STATUSES = ["confirmed", "cancelled", "completed", "no_show"] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const listBookingsQuerySchema = paginationSchema
  .extend({
    from: isoDateTime,
    to: isoDateTime,
    practitionerId: uuid.optional(),
    status: z.enum(BOOKING_STATUSES).optional(),
  })
  .superRefine((v, ctx) => {
    const days = (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000;
    if (days < 0) ctx.addIssue({ code: "custom", path: ["to"], message: "TO_BEFORE_FROM" });
    else if (days > BOOKINGS_LIST_MAX_DAYS) ctx.addIssue({ code: "custom", path: ["to"], message: "WINDOW_TOO_LARGE" });
  });

export interface ClinicBookingSettingsDto {
  slotIntervalMinutes: number;
  minNoticeMinutes: number;
  maxAdvanceDays: number;
  cancellationNoticeMinutes: number;
}

export interface WorkingHoursBlockDto {
  weekday: number;
  startMinute: number;
  endMinute: number;
}

export interface PractitionerDto {
  id: string;
  name: string;
  title: string;
  active: boolean;
  serviceIds: string[];
  workingHours: WorkingHoursBlockDto[];
  /** Whether a calendar feed URL has been issued (the URL itself is shown once, when issued). */
  hasCalendarFeed: boolean;
}

export interface TimeOffDto {
  id: string;
  practitionerId: string;
  startsAt: string;
  endsAt: string;
  reason: string;
}

export interface CustomerDto {
  id: string;
  fullName: string;
  email: string;
  phone: string;
}

export interface BookingDto {
  id: string;
  status: BookingStatus;
  source: "online" | "staff";
  startsAt: string;
  endsAt: string;
  service: { id: string; name: string; durationMinutes: number; priceCents: number; currency: string };
  practitioner: { id: string; name: string };
  customer: CustomerDto;
  notes: string;
  locale: Locale;
  createdAt: string;
  cancelledAt: string | null;
  cancelledBy: "customer" | "staff" | null;
  cancellationReason: string;
  rescheduledAt: string | null;
}

/**
 * Everything the clinic holds about one customer (GDPR art. 15/20), from
 * GET /customers/:id/export. Erasure (POST /customers/:id/erase) anonymises the
 * customer and their bookings' free text but keeps the bookings for statistics.
 */
export interface CustomerExportDto {
  exportedAt: string;
  clinic: { name: string };
  customer: { fullName: string; email: string; phone: string; createdAt: string };
  bookings: {
    startsAt: string;
    endsAt: string;
    status: BookingStatus;
    service: string;
    practitioner: string;
    notes: string;
    cancellationReason: string;
    createdAt: string;
    cancelledAt: string | null;
    rescheduledAt: string | null;
  }[];
  smsMessages: { toPhone: string; kind: string; status: string; createdAt: string }[];
}

/** What the public booking page may know about a clinic. */
export interface PublicClinicDto extends ClinicBookingSettingsDto {
  name: string;
  slug: string;
  timezone: string;
  defaultLocale: Locale;
  address: string;
  phone: string;
  description: string;
  /** False while the clinic is suspended or its subscription has lapsed. */
  acceptingBookings: boolean;
}

export interface PublicPractitionerDto {
  id: string;
  name: string;
  title: string;
}

export interface AvailabilitySlotDto {
  startsAt: string;
  endsAt: string;
  /** Practitioners free at this time; pick one or let the server choose. */
  practitionerIds: string[];
}

export interface AvailabilityDto {
  timezone: string;
  from: string;
  to: string;
  days: { date: string; slots: AvailabilitySlotDto[] }[];
}

/** A booking as seen by its customer through the secret manage link. */
export interface PublicBookingDto {
  id: string;
  status: BookingStatus;
  startsAt: string;
  endsAt: string;
  clinic: { name: string; slug: string; timezone: string };
  service: { name: string; durationMinutes: number; priceCents: number; currency: string };
  practitioner: { name: string };
  customer: { fullName: string; email: string };
  locale: Locale;
  /** Latest instant at which the customer may still cancel or reschedule online. */
  cancellableUntil: string;
  serviceId: string;
  practitionerId: string;
}

// ---------------------------------------------------------------------------
// Teams, additional clinics, public directory (CB-23).
// ---------------------------------------------------------------------------

export const INVITABLE_ROLES = ["admin", "practitioner"] as const;

export const createClinicSchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: clinicSlug,
  acceptTerms,
});

export const inviteMemberSchema = z.object({
  email,
  role: z.enum(INVITABLE_ROLES),
});

/** New users set a name and password; existing users confirm with their password. */
export const acceptInvitationSchema = z.object({
  fullName: personName.optional(),
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});

export const directoryQuerySchema = z.object({
  q: z.string().trim().max(80).default(""),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export interface MemberDto {
  userId: string;
  fullName: string;
  email: string;
  role: MembershipRole;
  joinedAt: string;
}

export interface InvitationDto {
  id: string;
  email: string;
  role: (typeof INVITABLE_ROLES)[number];
  createdAt: string;
  expiresAt: string;
}

/** What the invitation page shows before the visitor accepts. */
export interface PublicInvitationDto {
  clinicName: string;
  clinicSlug: string;
  email: string;
  role: (typeof INVITABLE_ROLES)[number];
  /** The address already has an account, so accepting means signing in. */
  userExists: boolean;
  state: "open" | "expired" | "accepted" | "revoked";
}

export interface DirectoryClinicDto {
  name: string;
  slug: string;
  address: string;
  description: string;
}

// ---------------------------------------------------------------------------
// Statistics (CB-25).
// ---------------------------------------------------------------------------

export const STATS_MAX_DAYS = 366;

export const statsQuerySchema = z.object({ from: isoDate, to: isoDate }).superRefine((v, ctx) => {
  const days = (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000;
  if (days < 0) ctx.addIssue({ code: "custom", path: ["to"], message: "TO_BEFORE_FROM" });
  else if (days >= STATS_MAX_DAYS) ctx.addIssue({ code: "custom", path: ["to"], message: "WINDOW_TOO_LARGE" });
});

export interface StatsDto {
  from: string;
  to: string;
  currency: string;
  bookings: {
    total: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    noShow: number;
    online: number;
    staff: number;
  };
  /** Value of completed bookings, and of everything still confirmed or completed. */
  revenue: { completedCents: number; expectedCents: number };
  byDay: { date: string; bookings: number; revenueCents: number }[];
  byPractitioner: {
    id: string;
    name: string;
    bookings: number;
    bookedMinutes: number;
    scheduledMinutes: number;
    /** bookedMinutes / scheduledMinutes, 0..1, or null when nothing was scheduled. */
    utilization: number | null;
  }[];
  byService: { id: string; name: string; bookings: number; revenueCents: number }[];
}

// ---------------------------------------------------------------------------
// Calendar feeds (CB-27).
// ---------------------------------------------------------------------------

export interface CalendarFeedDto {
  /** Secret subscription URL; shown once. */
  url: string;
}

// --- Platform administration and subscriptions (CB-33) ------------------------------------

export const SUBSCRIPTION_CURRENCIES = ["DKK", "EUR", "USD"] as const;
export type SubscriptionCurrency = (typeof SUBSCRIPTION_CURRENCIES)[number];
export const BILLING_INTERVALS = ["month", "year"] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];
export const SUBSCRIPTION_STATUSES = ["trialing", "active", "past_due", "cancelled"] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
export const CLINIC_STATUSES = ["active", "suspended"] as const;
export type ClinicStatus = (typeof CLINIC_STATUSES)[number];
/** Days an active subscription keeps working after its paid period ended. */
export const SUBSCRIPTION_GRACE_DAYS = 7;

/**
 * What the clinic can do right now, derived from clinic status and subscription:
 * trialing/active/past_due take bookings; expired/cancelled/suspended/none do not.
 */
export type AccessState = "trialing" | "active" | "past_due" | "expired" | "cancelled" | "suspended" | "none";
export const ACCEPTING_STATES: readonly AccessState[] = ["trialing", "active", "past_due"];

export interface PlanPriceDto {
  currency: SubscriptionCurrency;
  interval: BillingInterval;
  amountCents: number;
}

export interface PlanDto {
  id: string;
  code: string;
  name: string;
  description: string;
  trialDays: number;
  maxPractitioners: number | null;
  isDefault: boolean;
  active: boolean;
  sortOrder: number;
  prices: PlanPriceDto[];
  clinics: number;
}

/** A clinic's own view of its subscription (settings page, dashboard banner). */
export interface ClinicSubscriptionDto {
  planName: string;
  planCode: string;
  status: SubscriptionStatus;
  state: AccessState;
  currency: SubscriptionCurrency;
  interval: BillingInterval;
  amountCents: number | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  maxPractitioners: number | null;
}

export const planPriceSchema = z.object({
  currency: z.enum(SUBSCRIPTION_CURRENCIES),
  interval: z.enum(BILLING_INTERVALS),
  amountCents: z.number().int().min(0).max(100_000_000),
});
const planFields = {
  code: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "INVALID_SLUG")
    .min(2)
    .max(40),
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(1000),
  trialDays: z.number().int().min(0).max(365),
  maxPractitioners: z.number().int().min(1).max(10_000).nullable(),
  isDefault: z.boolean(),
  active: z.boolean(),
  sortOrder: z.number().int().min(0).max(1000),
  prices: z.array(planPriceSchema).max(6),
};
export const planSchema = z.object({
  ...planFields,
  description: planFields.description.default(""),
  trialDays: planFields.trialDays.default(14),
  maxPractitioners: planFields.maxPractitioners.default(null),
  isDefault: planFields.isDefault.default(false),
  active: planFields.active.default(true),
  sortOrder: planFields.sortOrder.default(0),
});
export const updatePlanSchema = z
  .object(planFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, "AT_LEAST_ONE_FIELD");

export const adminClinicsQuerySchema = paginationSchema.extend({
  q: z.string().trim().max(100).default(""),
  status: z.enum(CLINIC_STATUSES).optional(),
  state: z.enum(["trialing", "active", "past_due", "expired", "cancelled", "suspended"]).optional(),
});
export const suspendClinicSchema = z.object({ reason: z.string().trim().max(500).default("") });
export const updateSubscriptionSchema = z.object({
  planId: z.string().uuid(),
  currency: z.enum(SUBSCRIPTION_CURRENCIES),
  interval: z.enum(BILLING_INTERVALS),
  status: z.enum(SUBSCRIPTION_STATUSES),
  trialEndsAt: isoDateTime.nullable().default(null),
  currentPeriodEnd: isoDateTime.nullable().default(null),
  externalRef: z.string().trim().max(120).default(""),
  notes: z.string().trim().max(2000).default(""),
});
export const adminUsersQuerySchema = paginationSchema.extend({ q: z.string().trim().max(100).default("") });
export const adminSettingsSchema = z.object({
  adminIpAllowlist: z.array(z.string().trim().min(1).max(64)).max(50),
});
export const auditQuerySchema = paginationSchema.extend({
  targetType: z.enum(["clinic", "user", "plan", "subscription", "settings", "admin"]).optional(),
  targetId: z.string().trim().max(100).optional(),
});

export interface AdminSubscriptionDto {
  id: string;
  planId: string;
  planName: string;
  status: SubscriptionStatus;
  state: AccessState;
  currency: SubscriptionCurrency;
  interval: BillingInterval;
  amountCents: number | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  externalRef: string;
  notes: string;
}

export interface AdminClinicDto {
  id: string;
  name: string;
  slug: string;
  status: ClinicStatus;
  suspendedAt: string | null;
  suspendedReason: string;
  createdAt: string;
  ownerName: string;
  ownerEmail: string;
  members: number;
  practitioners: number;
  bookingsLast30Days: number;
  state: AccessState;
  subscription: AdminSubscriptionDto | null;
}

export interface AdminMemberDto {
  userId: string;
  fullName: string;
  email: string;
  role: MembershipRole;
  joinedAt: string;
}

export interface AdminClinicDetailDto extends AdminClinicDto {
  timezone: string;
  address: string;
  phone: string;
  listed: boolean;
  smsRemindersEnabled: boolean;
  bookingsTotal: number;
  services: number;
  membersList: AdminMemberDto[];
  audit: AuditEntryDto[];
}

export interface AdminUserDto {
  id: string;
  email: string;
  fullName: string;
  createdAt: string;
  memberships: { clinicId: string; clinicName: string; clinicSlug: string; role: MembershipRole }[];
}

export interface AuditEntryDto {
  id: number;
  actorEmail: string;
  action: string;
  targetType: "clinic" | "user" | "plan" | "subscription" | "settings" | "admin";
  targetId: string;
  details: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
}

export interface AdminOverviewDto {
  clinics: { total: number; active: number; suspended: number; newLast30Days: number };
  states: Record<AccessState, number>;
  users: number;
  bookings: { total: number; last30Days: number };
  messages: { emailsLast30Days: number | null; smsLast30Days: number };
  recentClinics: AdminClinicDto[];
}

/** The signed-in admin on the admin console (CONTROL_ORIGIN); admins are not clinic users. */
export interface AdminMeDto {
  admin: { id: string; email: string };
  /** Signed in with the initial password, which must be changed before anything else. */
  mustChangePassword: boolean;
  /** Origin of the clinic app, for links to booking pages. */
  appOrigin: string;
}

export interface AdminSettingsDto {
  adminIpAllowlist: string[];
  configuredAllowlist: string[];
  yourIp: string;
}
